import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  Container,
  SearchSection,
  InputWrapper,
  SearchInput,
  ClearButton,
  SearchButton,
  LoadingSpinner,
} from './OrdersTopBar.styled';

const MIN_SEARCH_LENGTH = 2;

const OrdersTopBar = ({
  searchValue = '',
  onSearchChange,
  onSearchSubmit,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const wasInputFocusedRef = useRef(false);

  const handleClear = () => {
    onSearchChange('');
    inputRef.current?.focus();
  };

  const handleFocus = () => {
    wasInputFocusedRef.current = true;
  };

  const handleBlur = () => {
    wasInputFocusedRef.current = false;
  };

  // Restore focus after loading completes
  useEffect(() => {
    if (!isLoading && wasInputFocusedRef.current && inputRef.current) {
      const timer = setTimeout(() => {
        if (inputRef.current && wasInputFocusedRef.current) {
          inputRef.current.focus();
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isLoading]);

  // ESC key handler
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && searchValue) {
        handleClear();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [searchValue]); // eslint-disable-line react-hooks/exhaustive-deps

  const isSearchDisabled = searchValue.length > 0 && searchValue.length < MIN_SEARCH_LENGTH;

  return (
    <Container>
      <SearchSection>
        <InputWrapper>
          <i className="mdi mdi-magnify" />
          <SearchInput
            ref={inputRef}
            type="text"
            placeholder={t('searchOrderPlaceholder')}
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyPress={(e) => 
              e.key === 'Enter' && !isSearchDisabled && !isLoading && onSearchSubmit()
            }
            disabled={isLoading}
            autoComplete="off"
          />
          {searchValue && !isLoading && (
            <ClearButton onClick={handleClear} type="button">
              <i className="mdi mdi-close-circle" />
            </ClearButton>
          )}
        </InputWrapper>

        <SearchButton 
          onClick={onSearchSubmit} 
          disabled={isSearchDisabled || isLoading}
        >
          {isLoading ? (
            <LoadingSpinner className="mdi mdi-loading" />
          ) : (
            <i className="mdi mdi-magnify" />
          )}
          {t('search')}
        </SearchButton>
      </SearchSection>
    </Container>
  );
};

OrdersTopBar.propTypes = {
  searchValue: PropTypes.string,
  onSearchChange: PropTypes.func,
  onSearchSubmit: PropTypes.func,
  isLoading: PropTypes.bool,
};

export default OrdersTopBar;

